/**
 * Support Agents -- Aaim-operator (/support/agents).
 *
 * platform.aaim.com:9303 (Aaim-operator SPA). Manages the SupportAgent
 * roster -- platform-admin staff assignable to consumer or FI-operator
 * cases. The auto-assign dispatcher (node 5, C3) selects from this
 * roster on case create: available agents under capacity, specialty
 * tags preferred (tags match case categories, e.g. "pledge").
 *
 *   GET  /api/v1/support/agents
 *   POST /api/v1/support/agents
 *
 * Ported from the orphaned admin-portal Support/SupportAgents.tsx
 * (node-5 plan item B3); audience platform-admin ONLY. No top-level
 * view-model interfaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BodyText,
  Button,
  Badge,
  Card,
  DataTable,
  type DataTableColumn,
  Input,
  Select,
  Alert,
  Dialog,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/typed-clients';
import {
  SUPPORT_SERVICE_MISSING_TITLE,
  SupportServiceMissingError,
  SupportServiceNotice,
  supportFailure,
} from './SupportServiceNotice';

type SupportAgent = components['schemas']['SupportAgent'];

const PLATFORM_ADMIN_ROLES = [
  'aaim_super_admin',
  'platform_admin',
  'e2e_test_admin',
] as const;

export function SupportAgentsPage() {
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.some((r) => roles.includes(r));

  const [agents, setAgents] = useState<SupportAgent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [newUserId, setNewUserId] = useState<string>('');
  const [newRoleScope, setNewRoleScope] = useState<string>('l1');
  const [newSpecialtyTags, setNewSpecialtyTags] = useState<string>('');
  const [newMaxConcurrent, setNewMaxConcurrent] = useState<string>('10');
  const [posting, setPosting] = useState<boolean>(false);
  const [postError, setPostError] = useState<string | null>(null);

  const loadAgents = useCallback(async () => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      const { data, error, response } = await supportPlatformClient.GET('/api/v1/support/agents');
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load agents');
      setAgents(data?.agents ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  const handleCreate = useCallback(async () => {
    if (!isPlatformAdmin) return;
    if (!newUserId.trim()) {
      setPostError('User id is required.');
      return;
    }
    setPosting(true);
    setPostError(null);
    try {
      const body: {
        userId: string;
        roleScope: 'l1' | 'l2' | 'l3' | 'specialist';
        specialtyTags?: string[];
        maxConcurrent?: number;
      } = {
        userId: newUserId.trim(),
        roleScope: newRoleScope as 'l1' | 'l2' | 'l3' | 'specialist',
      };
      const tags = newSpecialtyTags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (tags.length > 0) {
        body.specialtyTags = tags;
      }
      const parsed = parseInt(newMaxConcurrent, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        body.maxConcurrent = parsed;
      }
      const { error, response } = await supportPlatformClient.POST('/api/v1/support/agents', {
        body,
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to add agent');
      setDialogOpen(false);
      setNewUserId('');
      setNewRoleScope('l1');
      setNewSpecialtyTags('');
      setNewMaxConcurrent('10');
      await loadAgents();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) {
        setServiceMissing(true);
        setDialogOpen(false);
      } else {
        setPostError(err instanceof Error ? err.message : 'Failed to add agent');
      }
    } finally {
      setPosting(false);
    }
  }, [isPlatformAdmin, newUserId, newRoleScope, newSpecialtyTags, newMaxConcurrent, loadAgents]);

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4 space-y-3">
            <Alert type="error">
              The support agent roster is visible to platform-admin staff only.
            </Alert>
            <Link to="/support" className="text-primary hover:underline text-sm">
              &larr; Back to cases
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const columns: DataTableColumn<SupportAgent>[] = [
    {
      header: 'User ID',
      accessor: 'userId',
      cell: (_v, row) => <span className="font-mono text-xs">{row.userId}</span>,
    },
    {
      header: 'Role scope',
      accessor: 'roleScope',
      cell: (_v, row) => (
        <Badge variant="info" size="sm">
          {row.roleScope}
        </Badge>
      ),
    },
    {
      header: 'Specialties',
      accessor: 'specialtyTags',
      cell: (_v, row) =>
        Array.isArray(row.specialtyTags) && row.specialtyTags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.specialtyTags.map((t) => (
              <Badge key={t} variant="neutral" size="sm">
                {t}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-tertiary text-sm">-</span>
        ),
    },
    {
      header: 'Max concurrent',
      accessor: 'maxConcurrent',
      align: 'right',
    },
    {
      header: 'Available',
      accessor: 'isAvailable',
      cell: (_v, row) => (
        <Badge variant={row.isAvailable ? 'success' : 'neutral'} size="sm">
          {row.isAvailable ? 'available' : 'offline'}
        </Badge>
      ),
    },
  ];

  const roleScopeOptions = [
    { value: 'l1', label: 'L1' },
    { value: 'l2', label: 'L2' },
    { value: 'l3', label: 'L3' },
    { value: 'specialist', label: 'Specialist' },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <BodyText size="sm" color="secondary">
            Platform-admin staff assignable to consumer and FI-operator cases.
            New cases auto-assign to the least-loaded available agent; specialty
            tags matching the case category take precedence.
          </BodyText>
        </div>
        <Button variant="primary" onClick={() => setDialogOpen(true)} disabled={serviceMissing}>
          Add agent
        </Button>
      </div>

      {serviceMissing && <SupportServiceNotice />}
      {loadError && <Alert type="error">{loadError}</Alert>}

      <Card>
        <div className="p-4">
          <DataTable<SupportAgent>
            columns={columns}
            data={agents}
            rowKey="id"
            striped
            hoverable
            emptyMessage={
              loading ? 'Loading...' : serviceMissing ? `${SUPPORT_SERVICE_MISSING_TITLE}.` : 'No agents on the roster yet.'
            }
          />
        </div>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) {
            setDialogOpen(false);
            setPostError(null);
          }
        }}
        title="Add support agent"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={posting || !newUserId.trim()}
            >
              {posting ? 'Adding...' : 'Add agent'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {postError && <Alert type="error">{postError}</Alert>}
          <Input
            label="User ID"
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            placeholder="aaim-platform staff user UUID"
            required
          />
          <Select
            label="Role scope"
            options={roleScopeOptions}
            value={newRoleScope}
            onChange={(e) => setNewRoleScope(e.target.value)}
          />
          <Input
            label="Specialty tags"
            value={newSpecialtyTags}
            onChange={(e) => setNewSpecialtyTags(e.target.value)}
            placeholder="pledge, billing, kyc"
            helperText="Comma-separated case categories this agent specializes in. Auto-assign prefers specialty matches."
          />
          <Input
            label="Max concurrent cases"
            type="number"
            value={newMaxConcurrent}
            onChange={(e) => setNewMaxConcurrent(e.target.value)}
            helperText="Defaults to 10."
          />
        </div>
      </Dialog>
    </div>
  );
}
